import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Toast from "../../helpers/Toast";
import Layout from "../../components/Layout/Layout";
import { IoArrowBackSharp } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import { CircularProgress } from "@mui/material";
import { Row } from "reactstrap";
import InputComponent from "../../components/InputComponent";
import { AuditLogService } from "../../services/AuditLogService";
import { AuditDTO } from "../../models/AuditLog/AuditDTO";
import DiffViewer from "react-diff-viewer";
import { Utilities } from "../../helpers/Utilities";

export function AuditLogDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const intl = useIntl();
    const service = new AuditLogService();
    const [auditLog, setAuditLog] = useState<AuditDTO>({} as AuditDTO);
    const [loading, setLoading] = useState<boolean>(false);

    const getById = async () => {
        setLoading(true);
        const response = await service.GetById(Number(id));
        if (response.success === false || response.obj === null) {
            setLoading(false);
            Toast.Show("error", response.message);
            return;
        }
        setAuditLog(response.obj);
        setLoading(false);
    };

    useEffect(() => {
        getById();
    }, []);

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        <IoArrowBackSharp
                            className="pageTitleBackIcon"
                            onClick={() => navigate(-1)}
                        />
                        <FormattedMessage id="Screens.AuditLog.Title" />:
                    </span>
                </div>
                <div className="pageBody">
                    {loading && (
                        <div className="loading">
                            <span>
                                <FormattedMessage id="Common.Loading.DataLoading" />...
                            </span>
                            <CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                        </div>
                    )}

                    {!loading && !auditLog && (
                        <div style={{ color: "#E74C3C" }}>
                            <FormattedMessage id="Screens.User.UserDontExist" />
                        </div>
                    )}

                    {!loading && auditLog && (
                        <>
                            <Row style={{ margin: "0px", padding: "0px" }}>
                                <InputComponent
                                    xl={4}
                                    lg={4}
                                    md={5}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"text"}
                                    value={auditLog.tableName || ""}
                                    label={intl.formatMessage({
                                        id: "Screens.AuditLog.Info.TableName",
                                    })}
                                    readOnly={true}
                                />
                                <InputComponent
                                    xl={4}
                                    lg={4}
                                    md={5}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"text"}
                                    value={auditLog.primaryKey || ""}
                                    label={intl.formatMessage({
                                        id: "Screens.AuditLog.Info.PrimaryKey",
                                    })}
                                    readOnly={true}
                                />
                            </Row>
                            <Row style={{ margin: "0px", padding: "0px" }}>
                                <InputComponent
                                    xl={4}
                                    lg={4}
                                    md={5}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"text"}
                                    value={auditLog.type || ""}
                                    label={intl.formatMessage({
                                        id: "Screens.AuditLog.Info.Type",
                                    })}
                                    readOnly={true}
                                />
                                <InputComponent
                                    xl={4}
                                    lg={4}
                                    md={5}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"text"}
                                    value={auditLog.dateString}
                                    label={intl.formatMessage({
                                        id: "Screens.AuditLog.Info.DateTime",
                                    })}
                                    readOnly={true}
                                />
                            </Row>
                            <Row style={{ margin: "0px", padding: "0px" }}>
                                {auditLog.oldValues && auditLog.newValues && (
                                    <div style={{ width: "100%", height: "300px", overflowY: "auto", overflowX: "auto", marginTop: "20px", marginBottom: '20px' }}>
                                        <DiffViewer
                                            oldValue={Utilities.processValue(auditLog.oldValues)}
                                            newValue={Utilities.processValue(auditLog.newValues)}
                                            splitView={true}
                                            hideLineNumbers={false}
                                            styles={{
                                                diffContainer: {
                                                    whiteSpace: "pre-wrap",
                                                    wordWrap: "break-word",
                                                },
                                            }}
                                        />
                                    </div>
                                )}
                            </Row>
                            <Row style={{ margin: "0px", padding: "0px" }}>
                                {auditLog.affectedColumns && (
                                    <InputComponent
                                        xl={4}
                                        lg={4}
                                        md={5}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%", height: "150px" }}
                                        type={"textArea"}
                                        value={auditLog.affectedColumns
                                            ? auditLog.affectedColumns
                                                .replaceAll("[", "")
                                                .replaceAll(",", ",\n")
                                                .replaceAll("]", "")
                                            : ""}
                                        label={intl.formatMessage({
                                            id: "Screens.AuditLog.Info.AffectedColumns",
                                        })}
                                        readOnly={true}
                                    />
                                )}

                                {auditLog.oldValues !== null &&
                                    <InputComponent
                                        xl={4}
                                        lg={4}
                                        md={5}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%", height: "150px" }}
                                        type={"textArea"}
                                        value={auditLog.oldValues === null || auditLog.oldValues === undefined ? "" : JSON.stringify(auditLog.oldValues).replaceAll(",", ",\n").replaceAll("\\", "").replaceAll('\"{', "").replaceAll('}\"', '')}
                                        label={intl.formatMessage({
                                            id: "Screens.AuditLog.Info.OldValue",
                                        })}
                                        readOnly={true}
                                    />
                                }
                                {auditLog.newValues !== null &&
                                    <InputComponent
                                        xl={4}
                                        lg={4}
                                        md={5}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%", height: "150px" }}
                                        type={"textArea"}
                                        value={auditLog.newValues === null || auditLog.newValues === undefined ? "" : JSON.stringify(auditLog.newValues).replaceAll(",", ",\n").replaceAll("\\", "").replaceAll('\"{', "").replaceAll('}\"', '')}
                                        label={intl.formatMessage({
                                            id: "Screens.AuditLog.Info.NewValue",
                                        })}
                                        readOnly={true}
                                    />
                                }
                            </Row>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}
